import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, shortDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { title: "", body: "", cta_label: "", cta_to: "" };

export default function WhatsNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setItems((await api.get("/announcements")).data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and message are required");
    setBusy(true);
    try {
      await api.post("/announcements", {
        title: form.title, body: form.body,
        cta_label: form.cta_label || null, cta_to: form.cta_to || null,
      });
      toast.success("Announcement posted");
      setForm(EMPTY); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/announcements/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="overline text-muted-foreground flex items-center gap-1.5"><Megaphone size={14} /> Product updates</div>
          <h1 className="font-head font-bold text-2xl tracking-tight">What's New</h1>
          <p className="text-sm text-muted-foreground mt-1">A history of announcements for your workspace.</p>
        </div>
        {isAdmin && (
          <Button data-testid="announcement-new-btn" onClick={() => setOpen(true)}>
            <Plus size={16} className="mr-1.5" /> New announcement
          </Button>
        )}
      </div>

      <div className="relative pl-6 space-y-5 before:absolute before:left-1.5 before:top-2 before:bottom-2 before:w-px before:bg-border">
        {items.map((a) => (
          <div key={a.id} data-testid={`announcement-item-${a.id}`} className="relative bg-card border border-border rounded-md p-5">
            <span className="absolute -left-[1.4rem] top-6 h-2.5 w-2.5 rounded-full bg-gold ring-4 ring-background" />
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-head font-semibold text-foreground">{a.title}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {shortDate(a.created_at)} · {a.created_by_name}
                </div>
              </div>
              {isAdmin && !a.builtin && (
                <Button size="icon" variant="ghost" data-testid={`announcement-delete-${a.id}`} onClick={() => remove(a.id)}>
                  <Trash size={15} className="text-alert" />
                </Button>
              )}
            </div>
            <p className="text-sm text-foreground/90 mt-3">{a.body}</p>
            {a.cta_to && a.cta_label && (
              <button data-testid={`announcement-item-cta-${a.id}`} onClick={() => navigate(a.cta_to)} className="mt-3 text-sm font-medium text-primary hover:underline">
                {a.cta_label} →
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="announcement-dialog">
          <DialogHeader><DialogTitle>New announcement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="announcement-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. New reporting exports" />
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea data-testid="announcement-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="What's changed and why it matters…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Button label <span className="text-muted-foreground">(optional)</span></Label>
                <Input data-testid="announcement-cta-label" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="See billing" />
              </div>
              <div className="space-y-1.5">
                <Label>Button link <span className="text-muted-foreground">(optional)</span></Label>
                <Input data-testid="announcement-cta-to" value={form.cta_to} onChange={(e) => setForm({ ...form, cta_to: e.target.value })} placeholder="/billing" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="announcement-post" onClick={create} disabled={busy}>{busy ? "Posting…" : "Post announcement"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
